import type { CSSProperties } from 'react'
import { useEffect, useRef, useState } from 'react'

/**
 * Утилиты движения (docs/10 §1.4).
 *
 * Правило проекта: анимируем только `transform`/`opacity`, а живые числа
 * (ping, координаты) не анимируем вообще — счётчик здесь нужен для АГРЕГАТОВ
 * (сколько ботов в игре, uptime за сутки), которые меняются редко и где
 * «доезд» числа как раз подсказывает, что значение изменилось.
 */

/** `true`, если пользователь просил меньше движения. Реагирует на смену настройки. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => matchMediaReduced()?.matches ?? false)

  useEffect(() => {
    const query = matchMediaReduced()
    if (query === null) return
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  return reduced
}

function matchMediaReduced(): MediaQueryList | null {
  // jsdom в юнит-тестах не реализует matchMedia — без проверки падал бы рендер.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia('(prefers-reduced-motion: reduce)')
}

/**
 * Плавный «доезд» числа до нового значения на requestAnimationFrame.
 *
 * Почему не CSS-переход: анимируется само ЗНАЧЕНИЕ, а не свойство, поэтому
 * нужен кадр за кадром. Первый рендер значение не анимирует — при загрузке
 * страницы счётчик, ползущий от нуля, выглядит как «данные ещё не пришли».
 */
export function useCountUp(value: number, durationMs = 600): number {
  const reduced = usePrefersReducedMotion()
  const [shown, setShown] = useState(value)
  const fromRef = useRef(value)
  const firstRef = useRef(true)

  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false
      fromRef.current = value
      setShown(value)
      return
    }
    if (reduced || durationMs <= 0) {
      fromRef.current = value
      setShown(value)
      return
    }

    const from = fromRef.current
    if (from === value) return
    const start = performance.now()
    let frame = 0

    const tick = (now: number): void => {
      const progress = Math.min(1, (now - start) / durationMs)
      // easeOutCubic: быстрый старт, мягкая остановка — читается как «доехало».
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = from + (value - from) * eased
      setShown(next)
      if (progress < 1) {
        frame = requestAnimationFrame(tick)
        return
      }
      fromRef.current = value
      setShown(value)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [value, durationMs, reduced])

  return shown
}

/**
 * Индекс для лестницы появления: `style={stagger(i)}` рядом с классом
 * `anim-rise`. Ограничение сверху — чтобы 200-я карточка не появлялась через
 * 8 секунд.
 */
export function stagger(index: number, max = 12): CSSProperties {
  // Каст нужен потому, что типы React описывают только известные CSS-свойства,
  // а кастомные (`--i`) в них не входят — при этом браузер их принимает.
  return { '--i': Math.min(index, max) } as CSSProperties
}

/** То же для длины пути в анимации отрисовки линии графика. */
export function drawLength(length: number): CSSProperties {
  return { '--draw-length': length } as CSSProperties
}
